import {
  ReelClipSuggestion,
  ReelGapSuggestion,
  ReelReviewBoundary,
  ReelReviewGap,
  ReelReviewReport,
} from '../../api/structs/reelReview';
import { isTransitionName, Timeline } from '../../remotion/shared/metadata/timeline';
import { ReelSlide, TransitionName } from '../../remotion/shared/types';

/**
 * Pure logic for the whole-reel AI review's staged-preview flow: the boundary
 * map the "AI REVIEW" button POSTs (built from the editor's own timeline
 * selectors — never re-derived), the stable hash that guards against a stale
 * report, and the plan that turns a report's suggestions into the exact same
 * edits the trim handles / transition chip would produce.
 *
 * No store import here on purpose: this module is called BY editorStore.ts
 * (which executes the plan through its own `setSlideTrim`/`setSlideDuration`/
 * `setSlideTransition` actions), so importing the store back would cycle.
 */

export const round2 = (value: number): number => Math.round(value * 100) / 100;

// ==================== Boundary map ====================

/**
 * The absolute window a slide currently plays from INSIDE ITS OWN SOURCE FILE:
 * the trim pair for a video, [0, duration] for an image (which has no source
 * time). `trimEndSeconds` wins over `start + duration` when both exist — it is
 * the value the trim handles and the renderer read.
 */
const sourceWindow = (slide: ReelSlide, durationSeconds: number): { start: number; end: number } => {
  if (slide.mediaType !== 'video') return { start: 0, end: durationSeconds };
  const start = slide.trimStartSeconds ?? 0;
  return { start, end: slide.trimEndSeconds ?? start + durationSeconds };
};

/**
 * One boundary per timeline entry, carrying BOTH coordinate systems the review
 * needs and never mixing them up:
 *
 *  - `start`/`end` are the clip's window in the RENDERED REEL's playback time,
 *    straight off `computeTimeline`'s own `fromFrame`/`durationInFrames` (so a
 *    transition's overlap is already accounted for — consecutive windows
 *    overlap by exactly the transition between them, as the render does). This
 *    is the ONLY thing the judge sees: it watches the finished reel, and
 *    judge-reel-v1 tells it to turn "something is wrong at 8.7s" into a clip
 *    index by looking the timestamp up in this table.
 *  - `source_start`/`source_end` are that clip's absolute trim window inside
 *    its own file — invisible to the judge, and what `planClipChange` converts
 *    a cut-seconds suggestion against.
 *
 * Indices are 1-BASED on the wire, matching judge-reel-v1's own examples
 * ("clip 1", `{"index": 1}`); `planClipChange`/`planGapChange` map them back.
 *
 * `promptByMediaId` comes from the served clip (`media.images`/`media.ai_videos`
 * carry `.prompt`; the editor's own `mediaDrafts` do not).
 */
export const buildReelReviewBoundaries = (
  timeline: Timeline,
  fps: number,
  promptByMediaId: Record<string, string>
): ReelReviewBoundary[] =>
  timeline.entries.map((entry, position) => {
    const durationSeconds = entry.durationInFrames / fps;
    const source = sourceWindow(entry.slide, durationSeconds);
    return {
      index: position + 1,
      media_id: entry.slide.id,
      start: round2(entry.fromFrame / fps),
      end: round2((entry.fromFrame + entry.durationInFrames) / fps),
      source_start: round2(source.start),
      source_end: round2(source.end),
      duration: round2(durationSeconds),
      prompt: promptByMediaId[entry.slide.id] || '',
      subtitle: entry.slide.text || '',
    };
  });

export const NO_TRANSITION = 'none';

/**
 * One gap per adjacent boundary pair, carrying the EFFECTIVE transition
 * `computeTimeline` resolved (a declared transition too short to fit its
 * neighbours collapses to `none` here too, exactly as the chip shows it).
 */
export const buildReelReviewGaps = (timeline: Timeline, fps: number): ReelReviewGap[] =>
  timeline.entries.slice(0, -1).map((entry, position) => ({
    index: position + 1,
    from: position + 1,
    to: position + 2,
    transition: entry.transitionOut?.name ?? NO_TRANSITION,
    duration: round2((entry.transitionOut?.durationInFrames ?? 0) / fps),
  }));

// ==================== Stable hash ====================

/** djb2 over a canonical, explicitly-ordered-key JSON string. */
const djb2 = (input: string): string => {
  let hash = 5381;
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) + hash + input.charCodeAt(index)) >>> 0;
  }
  return hash.toString(16);
};

/**
 * Stable across re-serialization: every object below is rebuilt key-by-key in a
 * fixed order (never spread), and every number is rounded to 2 decimals — the
 * ONE contract the backend echoes `boundaries_hash` verbatim against, so a
 * fixed order here is what makes the stale-report guard trustworthy.
 */
export const computeBoundariesHash = (boundaries: ReelReviewBoundary[], gaps: ReelReviewGap[]): string => {
  const canonical = JSON.stringify({
    boundaries: boundaries.map((boundary) => ({
      index: boundary.index,
      media_id: boundary.media_id,
      start: round2(boundary.start),
      end: round2(boundary.end),
      source_start: round2(boundary.source_start),
      source_end: round2(boundary.source_end),
      duration: round2(boundary.duration),
      prompt: boundary.prompt,
      subtitle: boundary.subtitle,
    })),
    gaps: gaps.map((gap) => ({
      index: gap.index,
      from: gap.from,
      to: gap.to,
      transition: gap.transition,
      duration: round2(gap.duration),
    })),
  });
  return djb2(canonical);
};

// ==================== Staged-change plan ====================

export type StagedChangeKind = 'trim' | 'gap';

export interface StagedChange {
  id: string;
  kind: StagedChangeKind;
  /** The slide the highlight/revert applies to (the gap's LEFT slide, for a gap). */
  mediaId: string;
  /** Index into `clip_suggestions`/`gap_suggestions` — kept for the panel's key. */
  index: number;
  reason: string;
  /** Human-legible row label, e.g. "Trim 0.40s head, 0.10s tail". */
  summary: string;
  /** Exact prior values for the keys this change touches — undefined means "was absent". */
  before: Record<string, any>;
  /** What to hand the store's own trim/duration/transition actions. */
  after: {
    trimStart?: number;
    trimEnd?: number;
    duration?: number;
    transition?: TransitionName;
  };
}

/**
 * A suggestion the backend stores VERBATIM (unclamped, unvalidated) that fails
 * the client-side sanity check below — kept visible (greyed, no revert) so the
 * panel can say WHY it never touched anything, rather than silently vanishing.
 */
export interface IgnoredSuggestion {
  id: string;
  kind: StagedChangeKind;
  reason: string;
  /** What was proposed, unchanged — same wording a staged row would show. */
  summary: string;
}

export interface StagedChangePlan {
  staged: StagedChange[];
  ignored: IgnoredSuggestion[];
}

/** A transition's effective overlap is clamped into this range, never discarded for it. */
export const TRANSITION_MIN_DURATION_SECONDS = 0.2;
export const TRANSITION_MAX_DURATION_SECONDS = 0.8;

/** A trim that overshoots the 1s floor by up to this much is clamped, not discarded. */
const TRIM_OVERSHOOT_CLAMP_SECONDS = 0.2;
/** No trim suggestion may leave less than this much of the slide on screen. */
const MIN_REMAINING_SLIDE_SECONDS = 1;

/**
 * Wire indices are 1-BASED (judge-reel-v1 numbers its clips and gaps from 1),
 * so every lookup goes through here rather than raw `[index]`. A judge that
 * answers with 0, a fraction or a past-the-end index resolves to nothing and
 * its suggestion is silently dropped — never applied to the wrong slide.
 */
const atIndex = <T,>(items: T[], oneBasedIndex: number): T | undefined => {
  if (!Number.isInteger(oneBasedIndex) || oneBasedIndex < 1 || oneBasedIndex > items.length) {
    return undefined;
  }
  return items[oneBasedIndex - 1];
};

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const trimSummary = (cutHead: number, cutTail: number): string => {
  const parts: string[] = [];
  if (cutHead > 0) parts.push(`${cutHead.toFixed(2)}s head`);
  if (cutTail > 0) parts.push(`${cutTail.toFixed(2)}s tail`);
  return `Trim ${parts.length > 0 ? parts.join(', ') : '0.00s'}`;
};

type PlanResult =
  | { status: 'staged'; change: StagedChange }
  | { status: 'invalid'; ignored: IgnoredSuggestion }
  | { status: 'skip' };

const planClipChange = (
  suggestion: ReelClipSuggestion,
  boundaries: ReelReviewBoundary[],
  slidesById: Record<string, ReelSlide>,
  mediaDrafts: Record<string, Record<string, any>>
): PlanResult => {
  const boundary = atIndex(boundaries, suggestion.index);
  if (!boundary) return { status: 'skip' };
  const slide = slidesById[boundary.media_id];
  if (!slide) return { status: 'skip' };

  const id = `clip:${suggestion.index}`;
  let cutHead = numberOr(suggestion.trim_start, 0);
  let cutTail = numberOr(suggestion.trim_end, 0);

  // Cut-seconds are a magnitude off the current head/tail — never negative
  // (that would mean "add footage back", which no boundary describes).
  if (cutHead < 0 || cutTail < 0) {
    return {
      status: 'invalid',
      ignored: { id, kind: 'trim', reason: 'negative cut-seconds', summary: trimSummary(cutHead, cutTail) },
    };
  }
  if (cutHead <= 0 && cutTail <= 0) return { status: 'skip' }; // no-op suggestion

  // The slide must keep at least MIN_REMAINING_SLIDE_SECONDS on screen. A
  // small overshoot (the model rounding past the edge) is scaled back to fit
  // rather than thrown away; anything larger means the suggestion just does
  // not fit this slide.
  const maxAllowedTotal = boundary.duration - MIN_REMAINING_SLIDE_SECONDS;
  const totalCut = cutHead + cutTail;
  if (totalCut > maxAllowedTotal) {
    const overshoot = totalCut - maxAllowedTotal;
    if (maxAllowedTotal <= 0 || overshoot > TRIM_OVERSHOOT_CLAMP_SECONDS) {
      return {
        status: 'invalid',
        ignored: {
          id,
          kind: 'trim',
          reason: `cut leaves less than ${MIN_REMAINING_SLIDE_SECONDS}s of a ${boundary.duration.toFixed(2)}s slide`,
          summary: trimSummary(cutHead, cutTail),
        },
      };
    }
    const factor = maxAllowedTotal / totalCut;
    cutHead = round2(cutHead * factor);
    cutTail = round2(cutTail * factor);
  }

  const draft = mediaDrafts[boundary.media_id] || {};
  const summary = trimSummary(cutHead, cutTail);
  const base: Pick<StagedChange, 'id' | 'kind' | 'mediaId' | 'index' | 'reason' | 'summary'> = {
    id,
    kind: 'trim',
    mediaId: boundary.media_id,
    index: suggestion.index,
    reason: suggestion.reason || '',
    summary,
  };

  if (slide.mediaType === 'video') {
    return {
      status: 'staged',
      change: {
        ...base,
        before: { trim_start: draft.trim_start, trim_end: draft.trim_end, duration: draft.duration },
        after: {
          trimStart: round2(boundary.source_start + cutHead),
          trimEnd: round2(boundary.source_end - cutTail),
        },
      },
    };
  }

  // An image has no source-time trim window — only its on-screen duration.
  return {
    status: 'staged',
    change: {
      ...base,
      before: { duration: draft.duration },
      after: { duration: round2(boundary.duration - cutHead - cutTail) },
    },
  };
};

const planGapChange = (
  suggestion: ReelGapSuggestion,
  gaps: ReelReviewGap[],
  boundaries: ReelReviewBoundary[],
  mediaDrafts: Record<string, Record<string, any>>
): PlanResult => {
  const gap = atIndex(gaps, suggestion.index);
  if (!gap) return { status: 'skip' };
  const boundary = atIndex(boundaries, gap.from);
  if (!boundary) return { status: 'skip' };

  const id = `gap:${suggestion.index}`;
  if (suggestion.transition === undefined) return { status: 'skip' }; // nothing proposed

  if (!isTransitionName(suggestion.transition)) {
    return {
      status: 'invalid',
      ignored: {
        id,
        kind: 'gap',
        reason: `unknown transition "${suggestion.transition}"`,
        summary: `Transition → ${suggestion.transition}`,
      },
    };
  }

  // Never discarded for being out of range — the renderer's own floor/ceiling
  // for an overlap, so an over/under-shot duration is simply clamped into it.
  const clampedDuration =
    suggestion.duration === undefined
      ? undefined
      : round2(clamp(suggestion.duration, TRANSITION_MIN_DURATION_SECONDS, TRANSITION_MAX_DURATION_SECONDS));

  const draft = mediaDrafts[boundary.media_id] || {};
  const durationLabel = clampedDuration !== undefined ? ` (${clampedDuration.toFixed(2)}s)` : '';

  return {
    status: 'staged',
    change: {
      id,
      kind: 'gap',
      mediaId: boundary.media_id,
      index: suggestion.index,
      reason: suggestion.reason || '',
      summary: `Transition → ${suggestion.transition}${durationLabel}`,
      before: { transition_out: draft.transition_out, transition_duration: draft.transition_duration },
      after: { transition: suggestion.transition, duration: clampedDuration },
    },
  };
};

/**
 * Last one wins, position preserved: the backend stores the judge's report
 * verbatim, so a duplicate `index` (the model repeating itself) would
 * otherwise plan two `StagedChange`s sharing one id (`clip:0`/`gap:0`),
 * silently colliding in the panel's per-row keys and in `stagedChanges`
 * lookups by id.
 */
const dedupeByIndex = <T extends { index: number }>(items: T[]): T[] => {
  const byIndex = new Map<number, T>();
  items.forEach((item) => byIndex.set(item.index, item));
  return Array.from(byIndex.values());
};

/**
 * Turns a report's suggestions into the exact edits the timeline's own actions
 * would make — cut-seconds resolved against each slide's CURRENT absolute
 * trim/duration (the boundary map), same math the ReviewerPanel trim row uses.
 *
 * The backend stores the judge's report verbatim, unclamped — every suggestion
 * is re-validated here before it ever touches the store (see `dedupeByIndex`
 * above too). A suggestion that resolves to nothing (unknown index, a 0/0
 * trim, no transition proposed) is silently dropped; one that fails
 * validation (a negative cut, an overshoot past the clamp, an unknown
 * transition) is kept as `ignored` so the panel can show it, greyed, rather
 * than pretending it never arrived.
 */
export const planStagedChanges = (
  report: ReelReviewReport,
  boundaries: ReelReviewBoundary[],
  gaps: ReelReviewGap[],
  slides: ReelSlide[],
  mediaDrafts: Record<string, Record<string, any>>
): StagedChangePlan => {
  const slidesById = Object.fromEntries(slides.map((slide) => [slide.id, slide]));
  const staged: StagedChange[] = [];
  const ignored: IgnoredSuggestion[] = [];

  const collect = (result: PlanResult) => {
    if (result.status === 'staged') staged.push(result.change);
    else if (result.status === 'invalid') ignored.push(result.ignored);
  };

  dedupeByIndex(report.clip_suggestions || []).forEach((suggestion) =>
    collect(planClipChange(suggestion, boundaries, slidesById, mediaDrafts))
  );
  dedupeByIndex(report.gap_suggestions || []).forEach((suggestion) =>
    collect(planGapChange(suggestion, gaps, boundaries, mediaDrafts))
  );

  return { staged, ignored };
};
